<template>
  <v-form ref="form" class="form">
    <h2>{{ $t("cost-allocators.relations") }}</h2>
    <v-row>
      <v-col class="py-0" cols="6">
        <v-autocomplete
          v-model="costAllocator._provider.id"
          :items="costCentres"
          item-text="name"
          item-value="id"
          :label="$t('cost-allocators.providing-cost-centre')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('_provider.id')]
          "
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col class="py-0" cols="6">
        <v-autocomplete
          v-model="costAllocator._consumer.id"
          :items="costCentres"
          item-text="name"
          item-value="id"
          :label="$t('cost-allocators.consuming-cost-centre')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('_consumer.id')]
          "
          clearable
        ></v-autocomplete>
      </v-col>
    </v-row>
    <h2>{{ $t("cost-allocators.calculations") }}</h2>
    <v-row>
      <v-col cols="12" sm="4">
        <v-select
          v-model="costAllocator.type"
          :items="types"
          :label="$t('cost-allocators.type')"
          :loading="resourceLoading"
          :rules="
            resourceLoading ? [] : [rules.required, backEndValidation('type')]
          "
        ></v-select>
      </v-col>
      <template v-if="costAllocator.type === 'allocated'">
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="costAllocator.distribution_key"
            :label="$t('cost-allocators.distribution-key')"
            :loading="resourceLoading"
            :rules="
              resourceLoading
                ? []
                : [
                    rules.required,
                    rules.number({ min: 0 }),
                    backEndValidation('distribution_key')
                  ]
            "
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4">
          <v-combobox
            :items="symbols.map(i => i.symbol)"
            v-model="costAllocator.distribution_key_unit"
            :label="$t('cost-allocators.distribution-key-unit')"
            :loading="resourceLoading"
            :rules="
              resourceLoading
                ? []
                : [rules.required, backEndValidation('distribution_key_unit')]
            "
          ></v-combobox>
        </v-col>
      </template>
      <template v-else>
        <v-col cols="12" sm="4">
          <TreeSelector
            v-model="costAllocator.consumption_metering"
            :loading="resourceLoading"
            :rules="
              resourceLoading
                ? []
                : [rules.required, backEndValidation('consumption_metering')]
            "
            :label="$t('cost-allocators.countable')"
            :items="meterUnits"
            :options-for-tree-items="{
              groupKey: '_groups',
              canChooseGroup: false
            }"
            clearable
          ></TreeSelector>
        </v-col>
      </template>
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="costAllocator.ratio"
          :label="$t('cost-allocators.ratio')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [
                  rules.required,
                  rules.number({ min: 0, max: 1 }),
                  backEndValidation('ratio')
                ]
          "
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" class="factors">
        <v-flex
          v-for="factor in compensationFactorsForRender"
          class="factor"
          v-bind:key="factor.id"
        >
          <v-text-field
            :value="factor.key"
            :label="$t('cost-allocators.name')"
            :loading="resourceLoading"
            :rules="factor.key && !resourceLoading ? [rules.required] : []"
            v-on:input="val => setFactorKey(val, factor.key)"
            class="name"
          ></v-text-field>
          <v-text-field
            :value="factor.value"
            :label="$t('cost-allocators.factor')"
            :loading="resourceLoading"
            :rules="factor.key && !resourceLoading ? [rules.required] : []"
            prepend-inner-icon="mdi-multiplication"
            v-on:input="val => setFactorValue(val, factor.key)"
            class="factor"
          ></v-text-field>
          <v-btn icon class="btn" v-on:click="removeFactor(factor.key)">
            <v-icon>mdi-trash-can-outline</v-icon>
          </v-btn>
        </v-flex>
      </v-col>
    </v-row>
    <v-btn color="primary mr-3" v-on:click="save()" :loading="loading">
      <v-icon left>{{ $props.button_icon }}</v-icon>
      {{ $t(`cost-allocators.${$props.button_title}`) }}
    </v-btn>
  </v-form>
</template>

<script>
import { rules } from "../../../mixins/rules";
import TreeSelector from "../../../common/inputs/TreeSelector";
export default {
  name: "FormCostAllocator",
  mixins: [rules],
  components: { TreeSelector },
  data: () => ({
    loading: false,
    group_id: "",
    options: {
      lineNumbers: true,
      foldGutter: true,
      gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
      mode: "application/json"
    },
    compensation_factors_ids: {
      // key=>id
    },
    compensation_factors_counter: 1
  }),
  props: {
    dispatch: String,
    button_title: String,
    button_icon: String,
    costAllocator: {
      default: function() {
        return {
          type: "allocated",
          compensation_factors: {},
          _meter_unit: { id: null },
          _provider: { id: null },
          _consumer: { id: null }
        };
      }
    },
    resourceLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    compensationFactorsForRender() {
      let factorsId = this.compensation_factors_ids;
      let nextId = this.getNextFactorId;
      return [
        ...Object.keys(this.costAllocator.compensation_factors || {}).map(
          key => {
            let id = factorsId[key] || nextId();
            factorsId[key] = id;
            return {
              id,
              key,
              value: this.costAllocator.compensation_factors[key]
            };
          }
        ),
        {
          id: this.compensation_factors_counter,
          key: "",
          value: ""
        }
      ].sort((a, b) => a.id - b.id);
    },
    costCentres() {
      return this.$store.getters[
        "projectAccountingStore/costCentreStore/getList"
      ];
    },
    meterUnits() {
      let items = [];
      this.$store.getters["projectMeteringStore/meterUnitStore/getList"].map(
        mu => {
          let meterName = mu._meter ? mu._meter.name : "Without meter";
          if (this.costAllocator.type !== "allocated")
            items.push({
              name: `${mu.name}`,
              id: `${mu.id}`,
              icon: "mdi-google-analytics",
              disabled: this.costAllocator.type === "allocated",
              alwaysActive: this.costAllocator.type !== "allocated",
              _groups:
                mu._meter && mu._meter._group
                  ? [
                      ...(mu._meter._group._ancestors || []),
                      mu._meter._group,
                      { name: meterName, id: `${mu.id}_meter_${mu._meter.id}` }
                    ]
                  : []
            });
        }
      );
      return items;
    },
    types() {
      return this.$store.getters[
        "projectAccountingStore/costAllocatorStore/getTypes"
      ];
    },
    groups() {
      let groups = this.$store.getters[
        "projectMeteringStore/groupStore/getList"
      ];
      groups = groups.map(item => {
        item.selectName = [
          ...item._ancestors.reduce((ac, i) => {
            ac.push(i.name);
            return ac;
          }, []),
          item.name
        ].join(" > ");
        return item;
      });
      groups.sort(function(a, b) {
        let nameA = a.selectName.toUpperCase();
        let nameB = b.selectName.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      return groups;
    },
    symbols() {
      return this.$store.getters["presetStore/getSymbolPreset"];
    }
  },
  watch: {
    costAllocator() {
      if (
        this.costAllocator &&
        this.costAllocator._meter_unit &&
        this.costAllocator._meter_unit._meter
      ) {
        this.$store.dispatch("projectMeteringStore/meterStore/get", {
          id: this.costAllocator._meter_unit._meter.id,
          project_id: this.$route.params.project_id,
          onSuccess: res => {
            if (res._groups && res._groups.length > 0) {
              this.group_id = res._groups[res._groups.length - 1];
            }
          }
        });
      }
    }
  },
  methods: {
    save() {
      this.clearBackEndErrors();
      if (this.$refs.form.validate()) {
        this.loading = true;
        let item = {
          ...this.costAllocator,
          project_id: this.$route.params.project_id
        };

        this.$store
          .dispatch(this.$props.dispatch, {
            item,
            errorHandler: this.errorHandler.bind(this, () => {
              this.$refs.form.validate();
            }),
            onSuccess: item => {
              if (item !== "") {
                this.$store.commit(
                  "projectAccountingStore/costCentreStore/set",
                  {
                    item
                  }
                );
                this.$router.push({
                  name: "cost-allocator",
                  params: {
                    id: item.id
                  }
                });
              }
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    getNextFactorId() {
      return this.compensation_factors_counter++;
    },
    setFactorKey(new_key, old_key) {
      if (new_key !== old_key) {
        this.$set(
          this.compensation_factors_ids,
          new_key,
          this.compensation_factors_ids[old_key] || ""
        );
        delete this.compensation_factors_ids[old_key];
        this.$set(
          this.costAllocator.compensation_factors,
          new_key,
          this.costAllocator.compensation_factors[old_key] || ""
        );
        delete this.costAllocator.compensation_factors[old_key];
      }
    },
    setFactorValue(val, key) {
      this.costAllocator.compensation_factors[key] = +val;
    },
    removeFactor(key) {
      if (key) {
        this.$delete(this.compensation_factors_ids, key);
        this.$delete(this.costAllocator.compensation_factors, key);
      }
    }
  },
  mounted() {
    if (!this.costAllocator.id) {
      this.costAllocator._consumer.id = this.$route.query.consuming;
      this.costAllocator._provider.id = this.$route.query.providing;
    }
    this.$store.dispatch("projectAccountingStore/costCentreStore/getList", {
      params: {
        limit: -1,
        project_id: this.$route.params.project_id
      }
    });
    this.$store.dispatch("projectMeteringStore/groupStore/getList", {
      params: {
        limit: -1,
        project_id: this.$route.params.project_id
      }
    });
    this.$store.dispatch("projectMeteringStore/meterUnitStore/getList", {
      params: {
        limit: -1,
        project_id: this.$route.params.project_id
      }
    });
  }
};
</script>
<style lang="scss" scoped>
.factor {
  display: flex;
  align-items: center;
  justify-content: space-between;
  .btn {
    flex: 0 0 36px;
    margin-left: 15px;
  }
  .name {
    flex: 1 1;
  }
  .factor {
    margin-left: 15px;
    flex: 0 0 100px;
  }
}
</style>
